import React, { FC, Fragment } from "react";
import { Popover, Transition } from "@headlessui/react";
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import DatePicker from "react-datepicker";
import ButtonThird from "shared/Button/ButtonThird";
import ButtonPrimary from "shared/Button/ButtonPrimary";

interface DatesFilterProps {
  datesRange: [Date | null, Date | null];
  minStayMonths: number | null;
  onDateChange: (dates: [Date | null, Date | null]) => void;
  onMinStayChange: (months: number) => void;
  onClear: () => void;
  onApply: (close: () => void) => void;
  renderXClear: () => JSX.Element;
}

const TODAY = new Date();

const DatesFilter: FC<DatesFilterProps> = ({
  datesRange,
  minStayMonths,
  onDateChange,
  onMinStayChange,
  onClear,
  onApply,
  renderXClear,
}) => {
  const [start, end] = datesRange;
  const isFilterApplied = !!start || !!end || !!minStayMonths;
  const minimumStayOptions = [5, 6, 12];

  return (
    <Popover className="relative">
      {({ close }) => (
        <>
          <Popover.Button
            className={`flex items-center justify-center px-4 py-2 text-sm rounded-full border focus:outline-none transition-all ${
              isFilterApplied
                ? "bg-[#f5694b] text-white border-[#f5694b]"
                : "border-neutral-300 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 hover:border-neutral-400 dark:hover:border-neutral-500 bg-white dark:bg-neutral-800"
            }`}
          >
            <span>
              {start
                ? `${start.toLocaleDateString("en-US", { month: "short", day: "numeric" })}${
                    end ? ` - ${end.toLocaleDateString("en-US", { month: "short", day: "numeric" })}` : ""
                  }${minStayMonths ? ` • ${minStayMonths} mo min` : ""}`
                : "Dates"}
            </span>
            {isFilterApplied && renderXClear()}
            <CalendarIcon className="w-4 h-4 ml-2" />
          </Popover.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel className="absolute z-10 mt-3 left-0">
              <div className="rounded-2xl shadow-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 overflow-hidden">

                <style>{`
                  .ndp2 .react-datepicker {
                    display: flex !important;
                    border: none !important;
                    background: transparent !important;
                    font-family: inherit !important;
                  }
                  .ndp2 .react-datepicker__month-container {
                    float: none !important;
                    flex: 1 !important;
                    min-width: 260px !important;
                  }
                  .ndp2 .react-datepicker__month-container + .react-datepicker__month-container {
                    border-left: 1px solid #f3f4f6;
                  }
                  .ndp2 .react-datepicker__header {
                    padding: 0 !important;
                    border: none !important;
                    background: transparent !important;
                  }
                  .ndp2 .react-datepicker__day-names {
                    background: #fff4f1;
                    margin: 0 !important;
                    padding: 4px 0 !important;
                    display: flex !important;
                    justify-content: space-around !important;
                  }
                  .dark .ndp2 .react-datepicker__day-names {
                    background: #1f2937 !important;
                  }
                  .ndp2 .react-datepicker__day-name {
                    color: #f5694b !important;
                    font-weight: 700 !important;
                    font-size: 11px !important;
                    text-transform: uppercase !important;
                    flex: 1 !important;
                    text-align: center !important;
                    line-height: 2rem !important;
                  }
                  .ndp2 .react-datepicker__week {
                    display: flex !important;
                    justify-content: space-around !important;
                  }
                  .ndp2 .react-datepicker__month {
                    margin: 8px 4px !important;
                  }
                  .ndp2 .react-datepicker__day {
                    flex: 1 !important;
                    height: 2.25rem !important;
                    line-height: 2.25rem !important;
                    font-size: 13px !important;
                    margin: 1px !important;
                    border-radius: 50% !important;
                    text-align: center !important;
                    color: #111827 !important;
                  }
                  .dark .ndp2 .react-datepicker__day {
                    color: #e5e7eb !important;
                  }
                  .ndp2 .react-datepicker__day:hover:not(.react-datepicker__day--disabled) {
                    background: #fff4f1 !important;
                    color: #f5694b !important;
                    border-radius: 50% !important;
                  }
                  .ndp2 .react-datepicker__day--disabled {
                    color: #d1d5db !important;
                    background: transparent !important;
                    cursor: not-allowed !important;
                    opacity: 0.45 !important;
                  }
                  .dark .ndp2 .react-datepicker__day--disabled {
                    color: #4b5563 !important;
                    opacity: 1 !important;
                  }
                  .ndp2 .react-datepicker__day--outside-month {
                    color: #e5e7eb !important;
                    pointer-events: none;
                  }
                  .dark .ndp2 .react-datepicker__day--outside-month {
                    color: #374151 !important;
                  }
                  @media (max-width: 600px) {
                    .ndp2 .react-datepicker {
                      flex-direction: column !important;
                    }
                    .ndp2 .react-datepicker__month-container {
                      min-width: unset !important;
                      width: 100% !important;
                    }
                    .ndp2 .react-datepicker__month-container + .react-datepicker__month-container {
                      border-left: none !important;
                      border-top: 1px solid #f3f4f6 !important;
                    }
                  }
                `}</style>

                <div className="ndp2">
                  <DatePicker
                    selected={start}
                    onChange={onDateChange}
                    startDate={start}
                    endDate={end}
                    selectsRange
                    inline
                    monthsShown={2}
                    minDate={TODAY}
                    dayClassName={(date) => {
                      const isStart = start && date.toDateString() === start.toDateString();
                      const isEnd = end && date.toDateString() === end.toDateString();
                      const inRange = start && end && date > start && date < end;
                      const isToday = date.toDateString() === TODAY.toDateString();
                      if (isStart || isEnd) return "!bg-[#f5694b] !text-white font-bold";
                      if (inRange) return "!bg-[#fff4f1] dark:!bg-[#3d1f18] !text-[#c94a30] dark:!text-[#f5694b]";
                      if (isToday) return "font-bold !text-[#f5694b]";
                      return "";
                    }}
                    renderCustomHeader={({
                      monthDate,
                      customHeaderCount,
                      decreaseMonth,
                      increaseMonth,
                      prevMonthButtonDisabled,
                      nextMonthButtonDisabled,
                    }) => (
                      <div className="flex items-center justify-between px-3 py-2.5 bg-gradient-to-r from-[#f5694b] to-[#e2583d] relative overflow-hidden">
                        <div
                          className="absolute inset-0 pointer-events-none"
                          style={{
                            backgroundImage:
                              "repeating-linear-gradient(45deg, rgba(255,255,255,0.07) 0px, rgba(255,255,255,0.07) 1px, transparent 1px, transparent 10px)",
                          }}
                        />
                        <button
                          onClick={decreaseMonth}
                          disabled={prevMonthButtonDisabled}
                          type="button"
                          className={`relative z-10 w-7 h-7 flex items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition
                            ${customHeaderCount === 1 ? "invisible" : ""}
                            ${prevMonthButtonDisabled ? "opacity-40 cursor-not-allowed" : ""}`}
                        >
                          <ChevronLeftIcon className="w-4 h-4" />
                        </button>
                        <span className="relative z-10 text-white font-bold text-sm tracking-wide">
                          {monthDate.toLocaleString("en-US", { month: "long", year: "numeric" })}
                        </span>
                        <button
                          onClick={increaseMonth}
                          disabled={nextMonthButtonDisabled}
                          type="button"
                          className={`relative z-10 w-7 h-7 flex items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 transition
                            ${customHeaderCount === 0 ? "invisible" : ""}
                            ${nextMonthButtonDisabled ? "opacity-40 cursor-not-allowed" : ""}`}
                        >
                          <ChevronRightIcon className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  />
                </div>

                {/* Footer */}
                <div className="px-5 pb-5 pt-3 space-y-4 border-t border-neutral-100 dark:border-neutral-800">
                  <div>
                    <span className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">
                      Expected Minimum Stay
                    </span>
                    <div className="flex gap-2 flex-wrap">
                      {minimumStayOptions.map((months) => {
                        const isActive = minStayMonths === months;
                        const isDisabled = !start;
                        return (
                          <button
                            key={months}
                            type="button"
                            disabled={isDisabled}
                            onClick={() => onMinStayChange(months)}
                            className={`inline-flex items-center justify-center h-8 px-4 rounded-full border text-sm font-medium whitespace-nowrap transition ${
                              isDisabled
                                ? "opacity-40 cursor-not-allowed border-neutral-200 text-neutral-400"
                                : isActive
                                ? "border-[#f5694b] bg-[#f5694b] text-white"
                                : "border-neutral-300 text-neutral-700 hover:border-[#f5694b] hover:text-[#f5694b]"
                            }`}
                          >
                            {months} months
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <ButtonThird onClick={onClear} sizeClass="px-4 py-2">
                      Clear
                    </ButtonThird>
                    <ButtonPrimary onClick={() => onApply(close)} sizeClass="px-4 py-2">
                      Apply
                    </ButtonPrimary>
                  </div>
                </div>

              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

export default DatesFilter;